/*
  nudge.js — Trigger evaluation engine for the urgency nudge modal.

  Reads window.tarteNudgeConfig (set by modal-urgency-nudge.liquid) and registers
  listeners for each enabled trigger type. Once any trigger fires the modal is
  shown, all listeners are torn down, and the session is marked so the modal
  does not re-appear within the same browser session.

  Customer-group and locale eligibility are enforced before registering listeners
*/

(function () {
  'use strict';

  const SESSION_KEY = 'tarte_nudge_seen';

  function getSessionKey(nudgeId) {
    return SESSION_KEY + '_' + nudgeId;
  }

  function getShowCount(nudgeId) {
    try {
      return parseInt(sessionStorage.getItem(getSessionKey(nudgeId))) || 0;
    } catch (e) {
      return 0;
    }
  }

  function hasSeenNudgeThisSession(nudgeId, frequency) {
    const count = getShowCount(nudgeId);
    return count >= frequency;
  }

  function markNudgeAsSeen(nudgeId) {
    try {
      const next = getShowCount(nudgeId) + 1;
      sessionStorage.setItem(getSessionKey(nudgeId), String(next));
    } catch (e) {
    }
  }

  function getFrequencyForNudge(nudgeId) {
    const nudges = window.tarteNudgeConfig || [];
    for (let i = 0; i < nudges.length; i++) {
      if (nudges[i].id === nudgeId) return nudges[i].frequency_per_session || 1;
    }
    return 1;
  }

  /**
   * Normalises a customer_group value to a programmatic key so that both the
   * Shopify-admin human-readable labels and the programmatic keys are accepted.
   *
   * @param {string} raw
   * @returns {string}
   */
  function normalizeCustomerGroup(raw) {
    if (!raw) return 'all';
    const customerGroup = raw.toLowerCase().trim();
    if (customerGroup === 'all' || customerGroup === 'all customers') return 'all';
    if (customerGroup === 'loyalty' || customerGroup === 'loyalty customers' || customerGroup === 'logged in' || customerGroup === 'logged in customers') return 'loyalty';
    if (customerGroup === 'non-loyalty' || customerGroup === 'non-loyalty customers' || customerGroup === 'logged out' || customerGroup === 'logged out customers' || customerGroup === 'guest' || customerGroup === 'guests' || customerGroup === 'guest customers') return 'non-loyalty';
    if (customerGroup === 'new-customers' || customerGroup === 'new customers') return 'new-customers';
    if (customerGroup === 'returning-customers' || customerGroup === 'returning customers') return 'returning-customers';
    if (customerGroup === 'empty-cart' || customerGroup === 'empty cart' || customerGroup === 'customers with empty cart') return 'empty-cart';
    if (customerGroup === 'populated-cart' || customerGroup === 'populated cart' || customerGroup === 'customers with populated cart') return 'populated-cart';
    return customerGroup;
  }

  /**
   * Returns true if the current customer matches the nudge-level customer_group.
   * Mirrors isCustomerEligible() in custom-promotions.js:127–137.
   *
   * Supported groups (programmatic keys and human-readable admin labels both accepted):
   *   'all'                 — every visitor (default)
   *   'loyalty'             — logged-in customers
   *   'non-loyalty'         — guest / logged-out customers
   *   'new-customers'       — logged-in customers with 0 prior orders
   *   'returning-customers' — logged-in customers with 1+ prior orders
   *   'empty-cart'          — any visitor whose cart has 0 items (requires cart arg)
   *   'populated-cart'      — any visitor whose cart has 1+ items (requires cart arg)
   *
   * @param {Object}      nudge
   * @param {Object|null} cart  Result of /cart.js; required for cart-based groups.
   * @returns {boolean}
   */
  function isCustomerGroupEligible(nudge, cart) {
    const group = normalizeCustomerGroup(nudge.customer_group);
    if (group === 'all') return true;

    const isLoggedIn = window.customerIsLoggedIn === true;
    if (group === 'loyalty') return isLoggedIn;
    if (group === 'non-loyalty') return !isLoggedIn;
    if (group === 'new-customers') {
      return isLoggedIn && window.customerOrderCount === 0;
    }

    if (group === 'returning-customers') {
      return isLoggedIn && typeof window.customerOrderCount === 'number' && window.customerOrderCount > 0;
    }

    if (group === 'empty-cart') {
      if (!cart) return false;
      const cartQty = (cart.items || []).reduce(function (sum, item) { return sum + item.quantity; }, 0);
      return cartQty === 0;
    }

    if (group === 'populated-cart') {
      if (!cart) return false;
      const cartQty = (cart.items || []).reduce(function (sum, item) { return sum + item.quantity; }, 0);
      return cartQty > 0;
    }

    return false;
  }

  /**
   * Returns true if the current storefront country is included in the trigger's
   * locale list, or if the list is empty (no restriction).
   * Mirrors the locale check pattern in custom-promotions.js:159–169.
   *
   * If window.Shopify.country is not yet available the check is deferred to the
   * trigger registration step; callers must not cache the result across async gaps.
   *
   * @param {string[]} triggerLocales  Array of uppercase ISO country codes. Empty = all locales.
   * @returns {boolean}
   */
  function isTriggerLocaleEligible(triggerLocales) {
    if (!triggerLocales || triggerLocales.length === 0) {
      return true;
    }

    const countryCode = (window.Shopify && window.Shopify.country)
      ? window.Shopify.country.toUpperCase()
      : '';
    if (!countryCode) {
      return true;
    }

    const eligible = triggerLocales.indexOf(countryCode) !== -1;
    return eligible;
  }

  /**
   * Fetches the current cart from Shopify using the locale-aware root route and
   * cache: 'no-store', matching the pattern in window.updateTarteCartData.
   *
   * @returns {Promise<Object>}
   */
  async function fetchCart() {
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return fetch(root + 'cart.js', { cache: 'no-store' })
      .then(function (res) { return res.json(); });
  }

  var teardownFns = [];
  var displayedNudgeId = null;
  var triggeredNudgeIds = {};
  var resolveTriggeredNudgesTimer = null;

  function registerTeardown(fn) {
    teardownFns.push(fn);
  }

  function teardownAll() {
    var fns = teardownFns.slice();
    teardownFns = [];
    fns.forEach(function (fn) {
      try { fn(); } catch (e) {}
    });
  }

  /**
   * Resolves all nudges whose triggers fired in the current task, preserving
   * the order supplied by window.tarteNudgeConfig.
   */
  function resolveTriggeredNudges() {
    resolveTriggeredNudgesTimer = null;
    if (displayedNudgeId !== null) return;

    const nudges = window.tarteNudgeConfig || [];
    for (let i = 0; i < nudges.length; i++) {
      const nudge = nudges[i];
      if (!triggeredNudgeIds[nudge.id]) continue;
      if (hasSeenNudgeThisSession(nudge.id, nudge.frequency_per_session || 1)) continue;

      displayNudge(nudge.id);
      return;
    }
  }

  function showNudge(nudgeId) {
    if (displayedNudgeId !== null) return;
    if (hasSeenNudgeThisSession(nudgeId, getFrequencyForNudge(nudgeId))) return;

    triggeredNudgeIds[nudgeId] = true;

    if (resolveTriggeredNudgesTimer === null) {
      resolveTriggeredNudgesTimer = setTimeout(resolveTriggeredNudges, 0);
    }
  }

  /**
   * Tears down all pending trigger listeners, marks the session, then shows
   * the modal. Polls briefly for the element because connectedCallback may
   * have moved it to document.body after initial parse.
   *
   * @param {string} nudgeId  Value of active_nudge_setting.system.handle
   */
  function displayNudge(nudgeId) {
    if (displayedNudgeId !== null) return;

    const modalId = 'Nudge--' + nudgeId;

    function tryShow() {
      if (displayedNudgeId !== null) return false;

      const modal = document.getElementById(modalId);
      if (!modal) return false;

      displayedNudgeId = nudgeId;
      teardownAll();
      markNudgeAsSeen(nudgeId);

      if (typeof window.closeAllModalDialogs === 'function') {
        window.closeAllModalDialogs();
      }

      modal.setAttribute('open', '');
      modal.setAttribute('aria-hidden', 'false');

      return true;
    }

    if (tryShow()) return;

    if (typeof customElements !== 'undefined' && customElements.whenDefined) {
      customElements.whenDefined('modal-dialog').then(function () {
        if (tryShow()) return;
        let attempts = 0;
        const interval = setInterval(function () {
          if (tryShow()) {
            clearInterval(interval);
          } else if (++attempts > 20) {
            clearInterval(interval);
          }
        }, 50);
      });
    }
  }

  // ─── Trigger: inactivity ──────────────────────────────────────────────────

  /**
   * Shows the nudge after N seconds of user inactivity.
   * The timer resets on mousemove, keydown, touchstart, or scroll.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluateInactivityTrigger(trigger, nudgeId) {
    const seconds = Number(trigger.inactivity.time_length_seconds) || 0;
    if (seconds <= 0) return;

    let lastActivity = Date.now();
    const POLL_MS = 1000;

    function onActivity() {
      lastActivity = Date.now();
    }

    const activityEvents = ['mousemove', 'keydown', 'touchstart', 'scroll'];
    activityEvents.forEach(function (evt) {
      window.addEventListener(evt, onActivity, { passive: true });
    });

    const pollId = setInterval(function () {
      if (Date.now() - lastActivity >= seconds * 1000) {
        showNudge(nudgeId);
      }
    }, POLL_MS);

    registerTeardown(function () {
      clearInterval(pollId);
      activityEvents.forEach(function (evt) {
        window.removeEventListener(evt, onActivity);
      });
    });
  }

  // ─── Trigger: bag threshold ───────────────────────────────────────────────

  /**
   * Shows the nudge when the cart subtotal reaches the configured amount.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluateBagThresholdTrigger(trigger, nudgeId) {
    const threshold = Number(trigger.bag_threshold.amount) || 0;
    if (threshold <= 0) return;

    function checkCart(cart) {
      const rawCents = cart.subtotal_price != null ? cart.subtotal_price : (cart.total_price || 0);
      const subtotal = rawCents / 100;
      if (subtotal >= threshold) showNudge(nudgeId);
    }

    function onCartUpdated(e) {
      const cartData = e && e.detail && e.detail.cartData;
      if (cartData && typeof cartData.subtotal_price === 'number') {
        checkCart(cartData);
      } else {
        fetchCart().then(checkCart).catch(function () {});
      }
    }

    document.addEventListener('tarte:cart-data-updated', onCartUpdated);
    document.addEventListener('cartdrawer:open', onCartUpdated);
    registerTeardown(function () {
      document.removeEventListener('tarte:cart-data-updated', onCartUpdated);
      document.removeEventListener('cartdrawer:open', onCartUpdated);
    });

    fetchCart().then(checkCart).catch(function () {});
  }

  // ─── Trigger: bag quantity ────────────────────────────────────────────────

  /**
   * Shows the nudge when the total item count in the cart reaches the
   * configured quantity.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluateBagQuantityTrigger(trigger, nudgeId) {
    const minQty = Number(trigger.bag_quantity.quantity) || 0;
    if (minQty <= 0) return;

    function checkCart(cart) {
      const qty = (cart.items || []).reduce(function (sum, item) {
        return sum + item.quantity;
      }, 0);

      if (qty >= minQty) showNudge(nudgeId);
    }

    function onCartUpdated(e) {
      const cartData = e && e.detail && e.detail.cartData;
      if (cartData && Array.isArray(cartData.items)) {
        checkCart(cartData);
      } else {
        fetchCart().then(checkCart).catch(function () {});
      }
    }

    document.addEventListener('tarte:cart-data-updated', onCartUpdated);
    document.addEventListener('cartdrawer:open', onCartUpdated);
    registerTeardown(function () {
      document.removeEventListener('tarte:cart-data-updated', onCartUpdated);
      document.removeEventListener('cartdrawer:open', onCartUpdated);
    });

    fetchCart().then(checkCart).catch(function () {});
  }

  // ─── Trigger: page entry ──────────────────────────────────────────────────

  /**
   * Shows the nudge immediately if the current URL matches one of the
   * configured page handles. Strips the Shopify locale prefix (e.g. /en-au/)
   * using Shopify.routes.root before comparing, so localised routes work.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluatePageEntryTrigger(trigger, nudgeId) {
    const pages = trigger.page_entry.pages || [];
    if (!pages.length) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const pathname = window.location.pathname.slice(root.length - 1);

    const matches = pages.some(function (handle) {
      return pathname === '/pages/' + handle
        || pathname.indexOf('/pages/' + handle + '/') === 0;
    });

    if (matches) showNudge(nudgeId);
  }

  // ─── Trigger: collection entry ────────────────────────────────────────────

  /**
   * Shows the nudge immediately if the current URL matches one of the
   * configured collection handles.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluateCollectionEntryTrigger(trigger, nudgeId) {
    const collections = trigger.collection_entry.collections || [];
    if (!collections.length) return;

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const pathname = window.location.pathname.slice(root.length - 1);

    const matches = collections.some(function (handle) {
      return pathname === '/collections/' + handle
        || pathname.indexOf('/collections/' + handle + '/') === 0;
    });

    if (matches) showNudge(nudgeId);
  }

  // ─── Trigger: page load ───────────────────────────────────────────────────

  /**
   * Shows the nudge immediately on page load with no additional conditions.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluatePageLoadTrigger(trigger, nudgeId) {
    showNudge(nudgeId);
  }

  // ─── Trigger: PDP entry ───────────────────────────────────────────────────

  /**
   * Shows the nudge immediately if the current page is a product detail page.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluatePdpEntryTrigger(trigger, nudgeId) {
    if (window.location.pathname.indexOf('/products/') !== -1) {
      showNudge(nudgeId);
    }
  }

  // ─── Trigger: specific item not in cart ───────────────────────────────────

  /**
   * Shows the nudge when none of the specified product IDs are present in
   * the cart. Checked on load and again on every cart update.
   *
   * @param {Object}   trigger
   * @param {string}   nudgeId
   */
  function evaluateSpecificItemNotInCartTrigger(trigger, nudgeId) {
    const productIds = (trigger.specific_item_not_in_cart || []).map(String);
    if (!productIds.length) return;

    function checkCart(cart) {
      const cartProductIds = (cart.items || []).map(function (item) {
        return String(item.product_id);
      });
      const allAbsent = productIds.every(function (id) {
        return cartProductIds.indexOf(id) === -1;
      });
      if (allAbsent) showNudge(nudgeId);
    }

    function fetchAndCheck() {
      fetch('/cart.js')
        .then(function (res) { return res.json(); })
        .then(checkCart)
        .catch(function () {});
    }

    function onCartUpdated(e) {
      const cartData = e && e.detail && e.detail.cartData;
      if (cartData && Array.isArray(cartData.items)) {
        checkCart(cartData);
      } else {
        fetchCart().then(checkCart).catch(function () {});
      }
    }

    document.addEventListener('tarte:cart-data-updated', onCartUpdated);
    document.addEventListener('cartdrawer:open', onCartUpdated);
    registerTeardown(function () {
      document.removeEventListener('tarte:cart-data-updated', onCartUpdated);
      document.removeEventListener('cartdrawer:open', onCartUpdated);
    });

    fetchCart().then(checkCart).catch(function () {});
  }


  const CART_GROUPS = ['empty-cart', 'populated-cart'];

  function isTriggerEnabled(trigger) {
    return Boolean(
      (trigger.inactivity && trigger.inactivity.enabled)
      || (trigger.bag_threshold && trigger.bag_threshold.enabled)
      || (trigger.bag_quantity && trigger.bag_quantity.enabled)
      || (trigger.page_entry && trigger.page_entry.enabled)
      || (trigger.collection_entry && trigger.collection_entry.enabled)
      || (trigger.page_load && trigger.page_load.enabled)
      || (trigger.pdp_entry && trigger.pdp_entry.enabled)
      || (trigger.specific_item_not_in_cart && trigger.specific_item_not_in_cart.length)
    );
  }

  function isNudgeEligible(nudge, cart) {
    const hasEligibleTrigger = (nudge.triggers || []).some(function (trigger) {
      return isTriggerEnabled(trigger) && isTriggerLocaleEligible(trigger.locales);
    });

    return isCustomerGroupEligible(nudge, cart)
      && !hasSeenNudgeThisSession(nudge.id, nudge.frequency_per_session || 1)
      && hasEligibleTrigger;
  }

  function registerEligibleNudges(nudges, cart) {
    for (let i = 0; i < nudges.length; i++) {
      const nudge = nudges[i];
      if (isNudgeEligible(nudge, cart)) registerTriggers(nudge, cart);
    }
  }

  function registerTriggers(nudge, cart) {
    const triggers = nudge.triggers || [];
    for (let j = 0; j < triggers.length; j++) {
      let trigger = triggers[j];

      if (!isTriggerLocaleEligible(trigger.locales)) continue;

      if (trigger.inactivity && trigger.inactivity.enabled) {
        evaluateInactivityTrigger(trigger, nudge.id);
      }
      if (trigger.bag_threshold && trigger.bag_threshold.enabled) {
        evaluateBagThresholdTrigger(trigger, nudge.id);
      }
      if (trigger.bag_quantity && trigger.bag_quantity.enabled) {
        evaluateBagQuantityTrigger(trigger, nudge.id);
      }
      if (trigger.page_entry && trigger.page_entry.enabled) {
        evaluatePageEntryTrigger(trigger, nudge.id);
      }
      if (trigger.collection_entry && trigger.collection_entry.enabled) {
        evaluateCollectionEntryTrigger(trigger, nudge.id);
      }
      if (trigger.page_load && trigger.page_load.enabled) {
        evaluatePageLoadTrigger(trigger, nudge.id);
      }
      if (trigger.pdp_entry && trigger.pdp_entry.enabled) {
        evaluatePdpEntryTrigger(trigger, nudge.id);
      }
      if (trigger.specific_item_not_in_cart && trigger.specific_item_not_in_cart.length) {
        evaluateSpecificItemNotInCartTrigger(trigger, nudge.id);
      }
    }
  }

  function init() {
    const nudges = window.tarteNudgeConfig;
    if (!Array.isArray(nudges) || !nudges.length) return;

    const needsCart = nudges.some(function (nudge) {
      return CART_GROUPS.indexOf(normalizeCustomerGroup(nudge.customer_group)) !== -1;
    });

    if (needsCart) {
      fetchCart()
        .then(function (cart) {
          registerEligibleNudges(nudges, cart);
        })
        .catch(function () {});
    } else {
      registerEligibleNudges(nudges, null);
    }
  }

  function initializeNudges() {
    if (
      !Array.isArray(window.tarteNudgeConfig)
      || window.tarteNudgeConfig.length === 0
    ) {
      console.warn('[Nudge] No active nudge configuration found.');
      return;
    }

    init();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeNudges, {
      once: true,
    });
  } else {
    initializeNudges();
  }

  document.addEventListener('shopify:section:load', initializeNudges);
})();


function closeNudge() {
  const nudgeModal = document.querySelector('modal-dialog[id^="Nudge--"][open]');
  const closeButton = nudgeModal?.querySelector(
    'button[id^="ModalClose-Nudge--"]'
  );

  closeButton?.click();
}


document.addEventListener('click', (event) => {
  const cartButton = event.target.closest('[data-nudge-open-cart]');
  if (!cartButton) return;

  closeNudge();

  window.setTimeout(() => {
    document.querySelector('.header__icon--cart')?.click();
  }, 50);
});
